// src/components/PlanSubscriptionTable.js
import React from "react";
import Pagination from "../Common/Pagination";
import { useTranslation } from "../../contexts/LanguageContext";
import {
  MagnifyingGlassIcon,
  ChevronDownIcon,
  PlusIcon,
  EyeIcon,
  PencilIcon,
  TrashIcon
} from "@heroicons/react/24/outline";

const PlanSubscriptionTable = ({
  subscriptions = [],
  onEdit,
  onDelete,
  onAddNew,
  currentPage = 1,
  totalPages = 1,
  total = 0,
  itemsPerPage = 10,
  onPageChange
}) => {
    const { t } = useTranslation();
    console.log("Subscriptions in table:", subscriptions);

    const startIndex = (currentPage - 1) * itemsPerPage;
    
    return (
      <div className="bg-white rounded-[10px] border border-dashboardStroke shadow-[0px_9px_15px_rgba(0,0,0,0.07)] overflow-hidden">
        {/* Dark Header */}
        <div className="bg-dashboardDark h-16 px-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <h2 className="text-white font-semibold text-lg">{t("subscriptions.section.title")}</h2>
            <span className="text-white font-semibold">({total})</span>
          </div>

          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2 px-3 py-1.5 border border-white rounded">
              <MagnifyingGlassIcon className="w-[18px] h-[18px] text-white" />
              <input
                type="text"
                placeholder={t("common.search")}
                className="bg-transparent text-white placeholder-white/70 text-sm font-light outline-none w-28"
              />
            </div>

            <button className="flex items-center gap-2 px-3 py-1.5 border border-white rounded text-white text-sm font-light">
              <span>{t("common.sortBy")}</span>
              <ChevronDownIcon className="w-[18px] h-[18px] text-themeLightGray" />
            </button>

            <button className="flex items-center gap-2 px-3 py-1.5 border border-white rounded text-white text-sm font-light">
              <span>{t("common.dateRange")}</span>
              <ChevronDownIcon className="w-[18px] h-[18px] text-themeLightGray" />
            </button>

            <button
              onClick={onAddNew}
              className="flex items-center gap-2 px-4 py-1.5 bg-dashboardCoral rounded text-white text-sm font-medium"
            >
              <PlusIcon className="w-[18px] h-[18px]" />
              <span>{t("common.addNew")}</span>
            </button>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-dashboardStroke">
                <th className="px-4 py-4 text-left text-[17px] font-semibold text-dashboardText">{t("col.srNo")}</th>
                <th className="px-4 py-4 text-left text-[17px] font-semibold text-dashboardText">{t("col.name")}</th>
                <th className="px-4 py-4 text-left text-[17px] font-semibold text-dashboardText">{t("subscriptions.col.description")}</th>
                <th className="px-4 py-4 text-left text-[17px] font-semibold text-dashboardText">{t("subscriptions.col.priceMonthYear")}</th>
                <th className="px-4 py-4 text-left text-[17px] font-semibold text-dashboardText">{t("subscriptions.col.discountMonthYear")}</th>
                <th className="px-4 py-4 text-left text-[17px] font-semibold text-dashboardText">{t("col.status")}</th>
                <th className="px-4 py-4 text-left text-[17px] font-semibold text-dashboardText">{t("subscriptions.col.clip")}</th>
                <th className="px-4 py-4 text-left text-[17px] font-semibold text-dashboardText">{t("subscriptions.col.auction")}</th>
                <th className="px-4 py-4 text-left text-[17px] font-semibold text-dashboardText">{t("subscriptions.col.channel")}</th>
                <th className="px-4 py-4 text-left text-[17px] font-semibold text-dashboardText">{t("subscriptions.col.visibility")}</th>
                <th className="px-4 py-4 text-left text-[17px] font-medium text-dashboardText">{t("col.actions")}</th>
              </tr>
            </thead>
            <tbody>
              {subscriptions.map((plan, index) => (
                <tr 
                  key={plan._id}
                  className={`border-b border-dashboardStroke hover:bg-gray-50 transition-colors ${
                    index === 0 ? "shadow-[inset_3px_0px_0px_#FF7C17]" : ""
                  }`}
                >
                  <td className="px-4 py-4 whitespace-nowrap text-base font-normal text-dashboardDarkText">
                    {startIndex + index + 1}
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap text-base font-medium text-dashboardDarkText">
                    {plan.planName}
                  </td>
                  <td className="px-4 py-4 text-base font-normal text-dashboardDarkText max-w-xs">
                    <div className="break-words line-clamp-2">
                      {plan.description}
                    </div>
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap text-base font-normal text-dashboardDarkText">
                    <div>KWD {plan.planPricing.monthlyPrice}</div>
                    <div className="text-xs text-gray-500">KWD {plan.planPricing.yearlyPrice}</div>
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap text-base font-normal text-dashboardDarkText">
                    <div>KWD {plan.planPricing.monthlyDiscountedPrice}</div>
                    <div className="text-xs text-gray-500">KWD {plan.planPricing.yearlyDiscountedPrice}</div>
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap">
                    <span className={`inline-flex items-center px-2.5 py-1 text-xs font-medium rounded-full ${
                      plan.isPlanActive ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                    }`}>
                      <span className={`inline-block w-2 h-2 rounded-full mr-1.5 ${
                        plan.isPlanActive ? 'bg-green-500' : 'bg-gray-400'
                      }`} />
                      {plan.isPlanActive ? t("subscriptions.feature.active") : t("subscriptions.feature.closed")}
                    </span>
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap">
                    <span className={`inline-flex items-center px-2.5 py-1 text-xs font-medium rounded-full ${
                      plan.planFeatures.clips.isFeatureEnabled ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                    }`}>
                      <span className={`inline-block w-2 h-2 rounded-full mr-1.5 ${
                        plan.planFeatures.clips.isFeatureEnabled ? 'bg-green-500' : 'bg-gray-400'
                      }`} />
                      {plan.planFeatures.clips.isFeatureEnabled ? t("subscriptions.feature.active") : t("subscriptions.feature.closed")}
                    </span>
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap">
                    <span className={`inline-flex items-center px-2.5 py-1 text-xs font-medium rounded-full ${
                      plan.planFeatures.auctions.isFeatureEnabled ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                    }`}>
                      <span className={`inline-block w-2 h-2 rounded-full mr-1.5 ${
                        plan.planFeatures.auctions.isFeatureEnabled ? 'bg-green-500' : 'bg-gray-400'
                      }`} />
                      {plan.planFeatures.auctions.isFeatureEnabled ? t("subscriptions.feature.active") : t("subscriptions.feature.closed")}
                    </span>
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap">
                    <span className={`inline-flex items-center px-2.5 py-1 text-xs font-medium rounded-full ${
                      plan.planFeatures.channels.isFeatureEnabled ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                    }`}>
                      <span className={`inline-block w-2 h-2 rounded-full mr-1.5 ${
                        plan.planFeatures.channels.isFeatureEnabled ? 'bg-green-500' : 'bg-gray-400'
                      }`} />
                      {plan.planFeatures.channels.isFeatureEnabled ? t("subscriptions.feature.active") : t("subscriptions.feature.closed")}
                    </span>
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap text-base font-normal text-dashboardDarkText capitalize">
                    {plan.visibility}
                  </td>
                  <td className="px-4 py-4">
                    <div className="flex items-center gap-0.5">
                      <button className="p-1 hover:bg-gray-100 rounded transition-colors">
                        <EyeIcon className="w-6 h-6 text-dashboardDark" strokeWidth={1.3} />
                      </button>
                      <div className="w-px h-6 bg-dashboardStroke"></div>
                      <button 
                        onClick={() => onEdit(plan)}
                        className="p-1 hover:bg-gray-100 rounded transition-colors"
                      >
                        <PencilIcon className="w-6 h-6 text-dashboardDark" strokeWidth={1.3} />
                      </button>
                      <div className="w-px h-6 bg-dashboardStroke"></div>
                      <button 
                        onClick={() => onDelete(plan._id)}
                        className="p-1 hover:bg-gray-100 rounded transition-colors"
                      >
                        <TrashIcon className="w-6 h-6 text-dashboardCoral" strokeWidth={1.3} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        
        {totalPages > 1 && (
          <div className="px-4 py-4 border-t border-dashboardStroke">
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={onPageChange}
              total={total}
              itemsPerPage={itemsPerPage}
            />
          </div>
        )}
      </div>
    );
  };
  
  export default PlanSubscriptionTable;
